/**
 * Order → final sign-off (mockup docs/design/marketplace-hire/SignOff.dc.html,
 * "Final sign-off") and the install that follows it. Signing off approves and
 * pays the last stage and installs the app in the client's organization; when
 * the install didn't start (`result.install_error`), "Retry install" calls
 * installOrder.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { fmtDate, money } from '@/api/marketplace'
import { installOrder, requestStageChanges, signOffOrder, type OrderStage } from '@/api/marketplaceBuild'
import { Stepper } from '../MarketplaceShell'
import HostCallout from './HostCallout'
import { NoteField, addDays, firstName, hasMonthly, monthlyText, type OrderCtx } from './orderShared'

export function FinalSignOff(props: OrderCtx & { stage: OrderStage }) {
  const { order, act, busy, stage } = props
  const cur = order.currency
  const builderFirst = firstName(order.builder.person, order.builder.name)
  const monthly = hasMonthly(order.monthly) ? order.monthly : null
  const perPerson = monthly?.basis === 'person'
  const [people, setPeople] = useState<number>(Math.max(1, monthly?.seats || 1))
  const [rating, setRating] = useState(0)
  const [review, setReview] = useState('')
  const [agree, setAgree] = useState(false)
  const [name, setName] = useState('')
  const [asking, setAsking] = useState(false)
  const [note, setNote] = useState('')

  const earlier = order.stages.slice(1, -1)
  const earlierOk = earlier.filter((s) => s.status === 'approved' || s.status === 'released').length
  const latest = order.reports[0]
  const m = latest?.measured
  const testsRun = m ? (m.tests_passed || 0) + (m.tests_failed || 0) : 0
  const checks: { t: string; v: string; ok: boolean | null }[] = [
    { t: 'Earlier stages approved', v: `${earlierOk} of ${earlier.length}`, ok: earlierOk === earlier.length },
    ...(m && testsRun ? [{ t: 'Tests in the project', v: `${m.tests_passed} of ${testsRun} pass`, ok: !m.tests_failed }] : []),
    ...(latest ? [{ t: 'Latest progress report', v: `${latest.period_label} · ${latest.rag === 'green' ? 'On track' : latest.rag === 'amber' ? 'At risk' : 'Late'}`, ok: latest.rag === 'green' ? true : null }] : []),
    ...(order.reports.length ? [{
      t: 'Open requests to you', v: String(order.reports.reduce((n, r) => n + r.needs.filter((x) => !x.done).length, 0)),
      ok: order.reports.every((r) => r.needs.every((x) => x.done)) ? true : null,
    }] : []),
  ]
  const fixUntil = addDays(new Date().toISOString(), order.terms.fix_window_days)
  const happens = [
    { t: 'Installed for you', d: `Your own copy and database in ${order.client.name}, in My apps.` },
    { t: `${money(stage.amount_minor, cur)} paid to ${builderFirst}`, d: `The final ${stage.pct}%, by Twasta.` },
    ...(order.terms.fix_window_days > 0 ? [{ t: `${order.terms.fix_window_days} days of free fixes`, d: `Until about ${fmtDate(fixUntil)}, for anything that doesn’t work as agreed.` }] : []),
    ...(monthly ? [{
      t: 'Monthly price starts',
      d: perPerson
        ? `${monthlyText(monthly, cur)} (${money(monthly.price_minor * people, cur)} for ${people} ${people === 1 ? 'person' : 'people'}), from the install date.`
        : `${monthlyText(monthly, cur)}, from the install date.`,
    }] : []),
    { t: 'Your data stays yours', d: `${builderFirst} sees it only if you turn on support access.` },
  ]
  const canSign = order.can.signoff && agree && name.trim().length >= 3

  const sign = () => act(
    () => signOffOrder(order.id, { signed_name: name.trim(), people: perPerson ? people : undefined, rating: rating || undefined, review: review.trim() || undefined }),
    'Signed off',
  )
  const sendChanges = async () => {
    if (!note.trim()) return
    if (await act(() => requestStageChanges(order.id, stage.idx, note.trim()), `Change request sent to ${builderFirst}`)) { setAsking(false); setNote('') }
  }

  return (
    <section className="mkt-panel mkt-panel-accent mkt-col" aria-label="Final sign-off" style={{ gap: 18 }}>
      <div className="mkt-col" style={{ gap: 4 }}>
        <span className="mkt-kicker" style={{ alignSelf: 'flex-start' }}>Certificate of acceptance</span>
        <h2 style={{ fontSize: 22 }}>{order.title}</h2>
        <span className="mkt-muted">Order {order.order_no} · built by {order.builder.name} for {order.client.name}</span>
      </div>

      {stage.submit_note && (
        <div className="mkt-banner plain"><div><strong>Note from {builderFirst}</strong><span style={{ whiteSpace: 'pre-line' }}>“{stage.submit_note}”</span></div></div>
      )}

      <div className="mkt-row" style={{ justifyContent: 'space-between', padding: '12px 14px', borderRadius: 12, background: 'var(--color-bg)', border: '1px solid var(--color-border)' }}>
        <span className="mkt-col" style={{ gap: 0 }}>
          <strong>Final payment on sign-off</strong>
          <span className="mkt-faint">{stage.pct}%{stage.collected_minor > 0 ? ' · held by Twasta' : ''}</span>
        </span>
        <strong style={{ fontSize: 22 }}>{money(stage.amount_minor, cur)}</strong>
      </div>

      <div className="mkt-col" style={{ gap: 8 }}>
        <strong>Checked before you sign</strong>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
          {checks.map((c) => (
            <li key={c.t} className="mkt-row" style={{ gap: 10, flexWrap: 'nowrap', padding: '8px 10px', borderRadius: 10, border: '1px solid var(--color-border)' }}>
              <span aria-hidden="true" className={`mkt-pill ${c.ok === true ? 'ok' : c.ok === false ? 'bad' : 'warn'}`}>{c.ok === true ? '✓' : '!'}</span>
              <span className="mkt-col" style={{ gap: 0 }}><span className="mkt-muted" style={{ fontSize: 13 }}>{c.t}</span><strong>{c.v}</strong></span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mkt-col" style={{ gap: 8 }}>
        <strong>What happens when you sign off</strong>
        <ol style={{ margin: 0, paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {happens.map((h) => <li key={h.t}><strong>{h.t}</strong> — <span className="mkt-muted">{h.d}</span></li>)}
        </ol>
        {monthly && <HostCallout currency={cur} />}
      </div>

      {perPerson && monthly && (
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
          <span className="mkt-col" style={{ gap: 0 }}>
            <strong>People to start with</strong>
            <span className="mkt-faint">{money(monthly.price_minor, cur)} each a month · change any time in My apps</span>
          </span>
          <Stepper value={people} onChange={setPeople} label="people" />
        </div>
      )}

      <div className="mkt-col" style={{ gap: 8 }}>
        <strong>How did it go? <span className="mkt-faint">(optional — shown on {order.builder.name}’s profile)</span></strong>
        <div className="mkt-row" role="group" aria-label="Rating" style={{ gap: 4 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" aria-pressed={rating === n} aria-label={`${n} star${n > 1 ? 's' : ''}`}
              onClick={() => setRating(rating === n ? 0 : n)}
              style={{ font: 'inherit', fontSize: 26, lineHeight: 1, background: 'transparent', border: 0, cursor: 'pointer', padding: 4, color: n <= rating ? 'var(--color-warning)' : 'var(--color-text-faint)' }}>
              ★
            </button>
          ))}
        </div>
        <NoteField id="signoff-review" label="Your review" value={review} onChange={setReview} rows={3} />
      </div>

      <div className="mkt-col" style={{ gap: 10, borderTop: '1px solid var(--color-border)', paddingTop: 14 }}>
        <label className="mkt-row" style={{ gap: 8, cursor: 'pointer', flexWrap: 'nowrap', alignItems: 'flex-start' }}>
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 4 }} />
          <span>On behalf of {order.client.name} I accept {order.title} as delivered under the accepted quote.</span>
        </label>
        <div className="mkt-field" style={{ maxWidth: 380 }}>
          <label htmlFor="signoff-name">Type your full name</label>
          <input id="signoff-name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="mkt-row">
          <button type="button" className="btn btn-primary" disabled={busy || !canSign} onClick={sign}>
            {busy ? 'Signing off…' : 'Sign off and install'}
          </button>
          {order.can.changes && !asking && <button type="button" className="btn btn-secondary" onClick={() => setAsking(true)}>Ask for changes</button>}
        </div>
        <span className="mkt-faint">Not ready? Ask for changes instead — nothing more is paid until you sign.</span>
      </div>

      {asking && (
        <div className="mkt-col" style={{ gap: 10, border: '1px solid var(--color-border)', borderRadius: 12, padding: 14 }}>
          <NoteField id="final-changes" label={`What should ${builderFirst} change?`} value={note} onChange={setNote} rows={4} />
          <div className="mkt-row">
            <button type="button" className="btn btn-primary" disabled={busy || !note.trim()} onClick={sendChanges}>Send change request</button>
            <button type="button" className="btn btn-ghost" onClick={() => setAsking(false)}>Not now</button>
          </div>
        </div>
      )}
    </section>
  )
}

/** After sign-off: the install's outcome, and "Retry install" when it didn't start. */
export function InstallPanel({ order, act, busy }: OrderCtx) {
  const monthly = hasMonthly(order.monthly) ? order.monthly : null
  const perPerson = monthly?.basis === 'person'
  const [people, setPeople] = useState<number>(Math.max(1, monthly?.seats || 1))
  const err = order.result?.install_error as { message?: string } | null | undefined
  const installed = order.result?.installed as { subscription_id?: number } | null | undefined

  if (order.status === 'completed' && order.role === 'client') {
    return (
      <div className="mkt-banner ok" role="status">
        <div>
          <strong>{installed ? 'Signed off — the app is being installed' : 'Installed in your organization'}</strong>
          Your own copy and database in {order.client.name}. {order.terms.fix_window_days > 0
            ? `Free fixes until ${fmtDate(addDays(order.ended_at, order.terms.fix_window_days))} for anything that doesn’t work as agreed.` : ''}
        </div>
        <Link to="/marketplace/my-apps" className="btn btn-primary">Open it in My apps</Link>
      </div>
    )
  }
  if (!order.can.install && !err) return null
  return (
    <section className="mkt-panel mkt-panel-danger mkt-col" aria-label="Install">
      <h2>Signed off — the install didn’t start</h2>
      <span className="mkt-muted">{err?.message || 'The app is approved but not installed yet.'} Nothing about the sign-off is lost; try the install again.</span>
      {order.can.install ? (
        <>
          {perPerson && monthly && (
            <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
              <span>People to start with <span className="mkt-faint">· {money(monthly.price_minor, order.currency)} each a month</span></span>
              <Stepper value={people} onChange={setPeople} label="people" />
            </div>
          )}
          <div className="mkt-row">
            <button type="button" className="btn btn-primary" disabled={busy}
              onClick={() => act(() => installOrder(order.id, perPerson ? people : undefined), 'Installing — it appears in My apps')}>
              {busy ? 'Installing…' : 'Retry install'}
            </button>
          </div>
        </>
      ) : (
        <span className="mkt-faint">An administrator or project owner of {order.client.name} can retry it.</span>
      )}
    </section>
  )
}
